import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { Plus, Send, Trash2, Users } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { ChoiceSelect, HelpPopover, StatusChip } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import type {
  IReminderAudienceCount,
  IReminderPlan,
  IReminderStep,
  ReminderAudience,
  ReminderStepInput,
  ReminderStepKind,
  ReminderTiming
} from '../../api/cycle-reminder-contracts';
import { cycleRemindersApi } from '../../api/cycle-reminders-api';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { AdminPage, LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { TableFrame } from '../../components/table-frame';
import { mailLogPath, TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { ExcludedLine } from '../mail-log/excluded-line';
import { formatEt } from '../mail-templates/template-fields';
import { etInstant } from '../mail-templates/publish-dialog';

/**
 * A survey pin's reminder plan (SR-523 stage 3, `cycles.manage`): the invitation and reminders
 * the worker sends the pin's population — each at the pin's opening, a number of days before its
 * close, or on a date, at a time of day in Eastern Time — with a reminder template each and the
 * part of the population it reaches (not started, started and not submitted). The plan is edited
 * here and saved whole (`If-Match` its revision); a step that has fired is kept as it fired.
 *
 * A saved step can be counted before it is turned on — who it would reach now, and who it skips —
 * and sent at once (*Send now*, once the plan's edits are saved). A fired step's counts open its
 * campaign in the Email log. A step more than 24 hours past its time is Missed: the worker never
 * fires it, Send now still can. While bulk mail is off on the environment nothing goes out and Send
 * now is disabled.
 */

const MAX_STEPS = 10;

const TIMINGS: readonly { value: ReminderTiming['type']; label: string }[] = [
  { value: 'at_open', label: 'When the survey opens' },
  { value: 'before_close', label: 'Days before it closes' },
  { value: 'on_date', label: 'On a date' }
];

const KINDS: readonly { value: ReminderStepKind; label: string }[] = [
  { value: 'invitation', label: 'Invitation' },
  { value: 'reminder', label: 'Reminder' }
];

const AUDIENCES: readonly { value: ReminderAudience; label: string }[] = [
  { value: 'not_started', label: 'Not started' },
  { value: 'started', label: 'Started, not submitted' }
];

/** A step as the table edits it: a saved one keeps its id; `key` tells new ones apart. */
interface IDraftStep extends ReminderStepInput {
  key: string;
  saved: Nullable<IReminderStep>;
}

/** What a save sends of a step, in one key order, so two alike compare equal. */
function inputOf(step: ReminderStepInput): ReminderStepInput {
  const { id, kind, timing, sendTime, template, audience, enabled } = step;
  return { id, kind, timing, sendTime, template, audience, enabled };
}

function draftsOf(plan: IReminderPlan): IDraftStep[] {
  return plan.steps.map((step) => ({ ...inputOf(step), key: step.id, saved: step }));
}

const sameStep = (a: ReminderStepInput, b: ReminderStepInput) =>
  JSON.stringify(inputOf(a)) === JSON.stringify(inputOf(b));

/** "2027-01-31" — a day in ET, as a date input reads it. */
function etDay(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date(iso)).slice(0, 10);
}

/** When an edited step would fire, from the plan's dates — survey-api's `dueAt`, previewed. */
function dueOf(step: ReminderStepInput, plan: IReminderPlan): Nullable<Date> {
  if (step.timing.type === 'on_date') return etInstant(step.timing.date, step.sendTime);
  if (step.timing.type === 'before_close') {
    if (!plan.closesAt) return null;
    const close = new Date(`${etDay(plan.closesAt)}T00:00:00Z`);
    close.setUTCDate(close.getUTCDate() - step.timing.days);
    return etInstant(close.toISOString().slice(0, 10), step.sendTime);
  }
  if (!plan.opensAt) return null;
  return new Date(Math.max(new Date(plan.opensAt).getTime(), Date.now()));
}

export function CycleRemindersPage() {
  const { cycleCode = '', survey = '' } = useParams();
  const read = useAdminData((signal) => cycleRemindersApi.get(cycleCode, survey, signal), [cycleCode, survey]);
  const templates = useAdminData(
    async (signal) =>
      (await mailTemplatesApi.list(signal, { usage: 'reminders' })).filter(
        (template) => template.usages.includes('reminders') && !template.archivedAt
      ),
    []
  );
  const [generation, setGeneration] = React.useState(0);
  // Kept here: Send now's word outlives the plan read afresh under it.
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  return (
    <AdminPage
      title="Reminders"
      crumb={<Link to={TEAM_PATHS.cycles}>Cycles</Link>}
      lede={
        <span className="flex items-center gap-1">
          {cycleCode} · {survey}
          <HelpPopover label="About reminders" className="size-6">
            <p>
              The emails this survey&apos;s population gets in this cycle: an invitation, and reminders
              before the close. Each goes once, at its time in Eastern Time, to whoever has not
              submitted by then — those who unsubscribed or have no address are skipped.
            </p>
            <p>
              Count a step&apos;s recipients before turning it on. A step that has gone out is kept as
              it was sent; its counts open the campaign in the Email log.
            </p>
          </HelpPopover>
        </span>
      }
    >
      {read.error && <LoadError message={read.error} />}
      {!read.data && !read.error && <TableSkeleton rows={4} />}
      {read.data && (
        <Plan
          key={`${read.data.revision}:${generation}`}
          cycleCode={cycleCode}
          survey={survey}
          plan={read.data}
          templates={(templates.data ?? []).map((template) => ({ value: template.code, label: template.name }))}
          templatesRead={templates.data !== null}
          templatesError={templates.error}
          said={said}
          onSaid={setSaid}
          onSaved={(plan) => {
            read.adopt(plan);
            setGeneration((n) => n + 1);
          }}
          onReload={async () => {
            await read.reload();
            setGeneration((n) => n + 1);
          }}
        />
      )}
    </AdminPage>
  );
}

function Plan({
  cycleCode,
  survey,
  plan,
  templates,
  templatesRead,
  templatesError,
  said,
  onSaid: setSaid,
  onSaved,
  onReload
}: {
  cycleCode: string;
  survey: string;
  plan: IReminderPlan;
  templates: { value: string; label: string }[];
  /** Add a step waits for the templates: a step added before has none to start with. */
  templatesRead: boolean;
  templatesError: Nullable<string>;
  said: Nullable<string>;
  onSaid: (said: Nullable<string>) => void;
  onSaved: (plan: IReminderPlan) => void;
  onReload: () => Promise<void>;
}) {
  const [steps, setSteps] = React.useState<IDraftStep[]>(() => draftsOf(plan));
  const [busy, setBusy] = React.useState(false);
  const [problems, setProblems] = React.useState<Record<string, string>>({});
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const saved = draftsOf(plan);
  const changed = steps.length !== saved.length || steps.some((step, index) => !saved[index] || !sameStep(step, saved[index]));
  const open = plan.status === 'open';

  const set = (key: string, patch: Partial<ReminderStepInput>) =>
    setSteps((now) => now.map((step) => (step.key === key ? { ...step, ...patch } : step)));

  const add = () => {
    const kind: ReminderStepKind = steps.some((step) => step.kind === 'invitation') ? 'reminder' : 'invitation';
    setSteps((now) => [
      ...now,
      {
        key: `new-${Date.now()}`,
        kind,
        timing: kind === 'invitation' ? { type: 'at_open' } : { type: 'before_close', days: 7 },
        sendTime: '09:00',
        template: templates.find((template) => template.value.endsWith(`_${kind}`))?.value ?? templates[0]?.value ?? '',
        audience: kind === 'invitation' ? ['not_started'] : ['not_started', 'started'],
        // A new step starts off: it is counted first, then turned on.
        enabled: false,
        saved: null
      }
    ]);
  };

  const save = async () => {
    setBusy(true);
    setRefusal(null);
    setProblems({});
    setSaid(null);
    try {
      const next = await cycleRemindersApi.save(cycleCode, survey, plan.revision, steps.map(inputOf));
      onSaved(next);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 409 || error.isProblem(ProblemType.revisionConflict))) {
        setRefusal('Someone changed this plan meanwhile. Reload to see it — your changes here are not kept.');
      } else if (error instanceof ApiError && error.status === 400 && error.problem?.problems?.length) {
        const byTarget: Record<string, string> = {};
        for (const problem of error.problem.problems) byTarget[problem.target] = problem.reason;
        setProblems(byTarget);
        setRefusal('Some steps have problems: see the rows.');
      } else {
        setRefusal(refusalMessage(error, 'The plan could not be saved.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async (step: IDraftStep) => {
    if (!step.id) return;
    if (!window.confirm('Send this step now to everyone it reaches? It will not be sent again at its time.')) return;
    setBusy(true);
    setRefusal(null);
    try {
      await cycleRemindersApi.sendNow(cycleCode, survey, step.id);
      setSaid('Sent: the emails are queued and go out within the sending limits.');
      await onReload();
    } catch (error) {
      setRefusal(
        error instanceof ApiError && error.isProblem(ProblemType.bulkMailOff)
          ? 'Bulk mail is off here: reminders do not go out.'
          : error instanceof ApiError && error.status === 409
          ? 'This step has been sent already.'
          : refusalMessage(error, 'The step could not be sent.')
      );
    } finally {
      setBusy(false);
    }
  };

  const problemOf = (index: number, field: string) => problems[`steps[${index}].${field}`] ?? null;

  return (
    <div className="grid gap-4" data-reminder-plan={plan.revision}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.8125rem]">
        <StatusChip tone={open ? 'success' : 'neutral'}>{plan.status}</StatusChip>
        <span className="text-muted-foreground">Opens {plan.opensAt ? formatEt(plan.opensAt) : '—'}</span>
        <span className="text-muted-foreground">Closes {plan.closesAt ? formatEt(plan.closesAt) : 'no close date'}</span>
        <span className="ml-auto flex items-center gap-2">
          {changed && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setSteps(draftsOf(plan))}>
              Discard changes
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" disabled={busy || !templatesRead || steps.length >= MAX_STEPS} onClick={add}>
            <Plus className="size-4" aria-hidden />
            Add a step
          </Button>
          <Button type="button" size="sm" disabled={busy || !changed} onClick={() => void save()} data-save-plan="">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </span>
      </div>
      {!plan.bulkEnabled && (
        <p role="status" className="text-sm font-medium text-destructive" data-bulk-off="">
          Bulk mail is off here: reminders do not go out.
        </p>
      )}
      {templatesError && <p className="text-sm text-destructive">The reminder templates could not be read: {templatesError}</p>}
      {said && (
        <p role="status" className="text-sm">
          {said}
        </p>
      )}
      {refusal && (
        <p role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          {refusal}
          {refusal.startsWith('Someone') && (
            <Button type="button" variant="outline" size="sm" onClick={() => void onReload()}>
              Reload
            </Button>
          )}
        </p>
      )}
      <TableFrame>
        <Table aria-label="Steps" className="text-[0.8125rem]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Email</TableHead>
              <TableHead>When (ET)</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Who</TableHead>
              <TableHead className="w-16">On</TableHead>
              <TableHead className="min-w-56">Goes</TableHead>
              <TableHead className="w-44" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {steps.map((step, index) => (
              <StepRow
                key={step.key}
                step={step}
                plan={plan}
                templates={templates}
                busy={busy}
                problem={(field) => problemOf(index, field)}
                changed={!step.saved || !sameStep(step, step.saved)}
                cycleCode={cycleCode}
                survey={survey}
                onSet={(patch) => set(step.key, patch)}
                onRemove={() => setSteps((now) => now.filter((each) => each.key !== step.key))}
                bulkEnabled={plan.bulkEnabled}
                planChanged={changed}
                onSendNow={() => void sendNow(step)}
              />
            ))}
            {steps.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  No reminder yet — Add a step.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableFrame>
    </div>
  );
}

function StepRow({
  step,
  plan,
  templates,
  busy,
  problem,
  changed,
  cycleCode,
  survey,
  onSet,
  onRemove,
  bulkEnabled,
  planChanged,
  onSendNow
}: {
  step: IDraftStep;
  plan: IReminderPlan;
  templates: { value: string; label: string }[];
  busy: boolean;
  problem: (field: string) => Nullable<string>;
  changed: boolean;
  cycleCode: string;
  survey: string;
  onSet: (patch: Partial<ReminderStepInput>) => void;
  onRemove: () => void;
  bulkEnabled: boolean;
  /** Unsaved edits anywhere in the plan: Send now reads it afresh, so it waits for Save. */
  planChanged: boolean;
  onSendNow: () => void;
}) {
  const id = React.useId();
  const fired = step.saved?.firedAt ?? null;
  const locked = fired !== null;
  const due = step.saved && !changed ? (step.saved.dueAt ? new Date(step.saved.dueAt) : null) : dueOf(step, plan);
  const missed = !fired && !changed && step.saved?.missed === true;
  const timing = step.timing;
  const templateKnown = templates.some((template) => template.value === step.template);
  const errorId = (field: string) => (problem(field) ? `${id}-${field}` : undefined);
  const errorLine = (field: string) =>
    problem(field) && (
      <p id={errorId(field)} className="mt-1 text-xs text-destructive">
        {problem(field)}
      </p>
    );
  return (
    <TableRow data-reminder-step={step.id ?? step.key} className="align-top">
      <TableCell>
        <ChoiceSelect
          aria-label="Email"
          value={step.kind}
          disabled={locked}
          onChange={(kind) => onSet({ kind: kind as ReminderStepKind })}
          choices={KINDS}
          className="h-8"
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <ChoiceSelect
            aria-label="Timing"
            value={timing.type}
            disabled={locked}
            onChange={(type) =>
              onSet({
                timing:
                  type === 'at_open'
                    ? { type: 'at_open' }
                    : type === 'before_close'
                      ? { type: 'before_close', days: 7 }
                      : { type: 'on_date', date: plan.closesAt ? etDay(plan.closesAt) : '' }
              })
            }
            choices={TIMINGS}
            className="h-8 w-48"
          />
          {timing.type === 'before_close' && (
            <Input
              aria-label="Days before the close"
              type="number"
              min={0}
              max={120}
              value={timing.days}
              disabled={locked}
              aria-describedby={errorId('days')}
              onChange={(event) => onSet({ timing: { type: 'before_close', days: Math.max(0, Math.min(120, Number(event.target.value) || 0)) } })}
              className="h-8 w-16"
            />
          )}
          {timing.type === 'on_date' && (
            <Input
              aria-label="Date"
              type="date"
              value={timing.date}
              disabled={locked}
              aria-describedby={errorId('date')}
              onChange={(event) => onSet({ timing: { type: 'on_date', date: event.target.value } })}
              className="h-8 w-36"
            />
          )}
          {timing.type !== 'at_open' && (
            <Input
              aria-label="Time, Eastern"
              type="time"
              value={step.sendTime}
              disabled={locked}
              onChange={(event) => onSet({ sendTime: event.target.value })}
              className="h-8 w-28"
            />
          )}
        </div>
        {errorLine('timing')}
        {errorLine('days')}
        {errorLine('date')}
        {errorLine('sendTime')}
      </TableCell>
      <TableCell>
        <ChoiceSelect
          aria-label="Template"
          value={step.template}
          disabled={locked}
          onChange={(template) => onSet({ template })}
          choices={templateKnown || !step.template ? templates : [...templates, { value: step.template, label: step.template }]}
          className="h-8 w-48"
        />
        {errorLine('template')}
      </TableCell>
      <TableCell>
        <div className="grid gap-1" role="group" aria-labelledby={`${id}-who`}>
          <span id={`${id}-who`} className="sr-only">
            Who
          </span>
          {AUDIENCES.map((audience) => (
            <label key={audience.value} className="flex items-center gap-1.5 whitespace-nowrap">
              <Checkbox
                checked={step.audience.includes(audience.value)}
                disabled={locked}
                onCheckedChange={(checked) =>
                  onSet({
                    audience: checked
                      ? [...step.audience, audience.value]
                      : step.audience.filter((each) => each !== audience.value)
                  })
                }
              />
              {audience.label}
            </label>
          ))}
        </div>
        {errorLine('audience')}
      </TableCell>
      <TableCell>
        <Checkbox
          aria-label="Send at its time"
          checked={step.enabled}
          disabled={locked}
          onCheckedChange={(checked) => onSet({ enabled: checked === true })}
        />
      </TableCell>
      <TableCell className="text-xs">
        <Goes step={step} fired={fired} missed={missed} due={due} changed={changed} />
        {step.saved && !changed && !locked && (
          <AudienceCount cycleCode={cycleCode} survey={survey} stepId={step.saved.id} />
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap justify-end gap-1">
          {step.saved && !locked && !changed && plan.status === 'open' && (
            <span title={planChanged ? 'Save or discard the changes first' : undefined}>
              <Button type="button" variant="outline" size="sm" disabled={busy || !bulkEnabled || planChanged} onClick={onSendNow}>
                <Send className="size-4" aria-hidden />
                Send now
              </Button>
            </span>
          )}
          {!locked && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              aria-label="Remove the step"
              disabled={busy}
              onClick={onRemove}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/** When the step goes, went, or why it never will; a sent one's counts, to its campaign. */
function Goes({
  step,
  fired,
  missed,
  due,
  changed
}: {
  step: IDraftStep;
  fired: Nullable<string>;
  missed: boolean;
  due: Nullable<Date>;
  changed: boolean;
}) {
  const counts = step.saved?.counts ?? null;
  const campaignId = step.saved?.campaignId ?? null;
  if (fired) {
    return (
      <span className="grid gap-0.5">
        <StatusChip tone="success">Sent {formatEt(fired)}</StatusChip>
        {counts && (
          <Link
            to={campaignId ? mailLogPath({ campaign: campaignId }) : mailLogPath()}
            className="text-[var(--color-sapphire)] hover:underline"
            data-step-counts=""
          >
            {counts.sent} sent · {counts.queued} to send · {counts.failed} failed · {counts.skipped} skipped
          </Link>
        )}
      </span>
    );
  }
  if (missed) {
    return (
      <span className="grid gap-0.5">
        <span className="flex items-center gap-1">
          <StatusChip tone="attention">Missed</StatusChip>
          <HelpPopover label="About a missed step" className="size-6">
            <p>
              The worker never sends a step more than 24 hours after its time, so this one stays
              unsent. Send now still sends it.
            </p>
          </HelpPopover>
        </span>
        {due && <span className="text-muted-foreground">was due {formatEt(due.toISOString())}</span>}
      </span>
    );
  }
  if (!due) {
    return <span className="text-muted-foreground">{step.timing.type === 'before_close' ? 'Needs a close date' : 'When the survey opens'}</span>;
  }
  return (
    <span className={cn('grid', !step.enabled && 'text-muted-foreground')}>
      <span>{formatEt(due.toISOString())}</span>
      <span className="text-muted-foreground">{!step.enabled ? 'off — not sent' : changed ? 'after saving' : 'scheduled'}</span>
    </span>
  );
}

/** A saved step's dry run: who it would reach now, read on a press. */
function AudienceCount({ cycleCode, survey, stepId }: { cycleCode: string; survey: string; stepId: string }) {
  const [count, setCount] = React.useState<Nullable<IReminderAudienceCount>>(null);
  const [state, setState] = React.useState<'idle' | 'reading' | 'failed'>('idle');
  const read = async () => {
    setState('reading');
    try {
      setCount(await cycleRemindersApi.audience(cycleCode, survey, stepId));
      setState('idle');
    } catch {
      setState('failed');
    }
  };
  if (count) {
    const reached = count.total - count.suppressed - count.noAddress;
    return (
      <span className="mt-1 grid text-muted-foreground" data-step-audience="">
        <span className="font-medium text-foreground">{reached.toLocaleString('en-US')} would get it now</span>
        <span>
          {count.notStarted} not started · {count.started} started
        </span>
        <span>
          {count.suppressed} unsubscribed · {count.noAddress} without an address
        </span>
        <ExcludedLine excluded={count.excluded} />
      </span>
    );
  }
  return (
    <span className="mt-1 flex items-center gap-1">
      <Button type="button" variant="link" size="sm" className="h-auto gap-1 p-0 text-xs" disabled={state === 'reading'} onClick={() => void read()}>
        <Users className="size-3.5" aria-hidden />
        {state === 'reading' ? 'Counting…' : 'Count the recipients'}
      </Button>
      {state === 'failed' && <span className="text-destructive">Could not count.</span>}
    </span>
  );
}
